<template>
    <div class="country__svg" data-country></div>
    <p class="country__map-attribution">
        Kartdata: <a href="https://www.kartverket.no/" target="_blank" rel="noopener">Kartverket</a> (CC BY 4.0)
    </p>

    <div class="country__overview">
        <div class="country__overview-top">         
            <div class="country__overview-subtitle">    
                Toppliste: {{ selectedYear }} - {{ selectedMunicipality != 0 ? getMunicipality(selectedMunicipality) : 'Hele Norge' }} -
                            {{ selectedItem != 0 ? getItem(selectedItem) : 'Alle typer' }}
            </div>
            <div>
                Fylkene og kommunene som kan velges er de hvor det har vært plukket {{ groupName.toLowerCase() }} i.
            </div>
        </div>
        <div class="country__overview-columns">
            <div class="country__overview-column">
                <div class="country__list" ref="listBox">
                    <ul class="country__list-items">
                        <li v-if="loadingToplist" class="country__list-loading">
                            <div class="country__list-spinner"></div>
                            Laster toppliste...
                        </li>
                        <template v-else>
                            <li class="country__list-item" v-if="topList.length == 0">
                                <div class="country__list-name">Ingen registreringer er funnet. <br><br> Dette kan skyldes at ingen har registrert plukking i kommunen, for typen, eller at de som har plukket ønsker å være skjult i topplistene.</div>
                            </li>
                            <li class="country__list-item" v-for="user of rankedTopList" v-bind:key="user.id">
                                <div class="country__list-amount">{{ user[selectedUnit].toFixed(1) }} {{ selectedUnit === 'liters' ? 'L' : 'kg' }}</div>
                                <div class="country__list-name"
                                     @mouseenter="showUserDetails(user.id, $event)"
                                     @mouseleave="onNameMouseLeave"
                                     @touchstart="showUserDetails(user.id, $event)"
                                     @click="toggleUserDetailsOnMobile(user.id, $event)">{{ formatName(user.name) }}</div>
                            </li>
                        </template>
                    </ul>
                    <Paginator
                        v-if="topListTotal > topListPerPage"
                        class="country__list-paginator"
                        :rows="topListPerPage"
                        :totalRecords="topListTotal"
                        :first="topListPage * topListPerPage"
                        template="PrevPageLink PageLinks NextPageLink"
                        @page="event => $emit('update-toplist-page', event.page)"
                    ></Paginator>
                    <div class="country__list-note">
                        Topplisten viser bare brukere som har valgt å være synlig for andre.
                    </div>
                </div>
                <div class="country__tooltip" v-if="showTooltip" :style="tooltipStyles" 
                     @mouseenter="keepTooltipVisible" 
                     @mouseleave="onTooltipMouseLeave">
                    <div class="country__tooltip-arrow" :class="tooltipArrowClass"></div>
                    <div class="country__tooltip-content">
                        <div v-if="loadingUserDetails" class="country__tooltip-loading">
                            <div class="country__tooltip-spinner"></div>
                            Laster aktiviteter...
                        </div>
                        <div v-else-if="userDetails && userDetails.length > 0">
                            <div class="country__tooltip-header">
                                <h4 class="country__tooltip-title">🍓 Aktiviteter ({{ userDetails.length }})</h4>
                            </div>
                            <div class="country__tooltip-list">
                                <div v-for="detail in userDetails.slice(0, 8)" :key="detail.id"
                                    class="country__tooltip-item">
                                    <div class="country__tooltip-item-header">
                                        <span class="country__tooltip-date"><img src="/icons/calendar.svg" class="country__tooltip-date-icon" alt="">{{ formatDate(detail.date) }}</span>
                                        <span class="country__tooltip-amount">{{ detail.amount }} {{ detail.uom.name
                                            }}</span>
                                    </div>
                                    <div class="country__tooltip-item-details">
                                        <span class="country__tooltip-location"><img src="/icons/grademarker_1.svg" class="country__tooltip-location-icon" alt="">{{ detail.location_name }}</span>
                                        <span class="country__tooltip-type">
                                            <img v-if="detail.illustration_src" :src="detail.illustration_src" :alt="detail.illustration_alt" class="country__tooltip-type-icon">
                                            <span>{{ detail.type_name }}</span>
                                        </span>
                                    </div>
                                </div>
                                <div v-if="userDetails.length > 8" class="country__tooltip-more">
                                    ... og {{ userDetails.length - 8 }} flere registreringer
                                </div>
                            </div>
                        </div>
                        <div v-else class="country__tooltip-empty">
                            <div class="country__tooltip-empty-icon">🤷‍♂️</div>
                            Ingen registreringer funnet for {{ selectedYear }}
                        </div>
                    </div>
                </div>
            </div>
            <div class="country__overview-column country__overview-column--selects">
                <div class="country__select-wrapper">
                    <Select
                        :modelValue="selectedItem"
                        :options="itemSelectOptions"
                        optionLabel="name"
                        optionValue="id"
                        inputId="selectedItem"
                        style="width: 100%"
                        @update:modelValue="itemId => $emit('update-selected-item', itemId)"
                    >
                        <template #value>
                            <span v-if="selectedItemOption" class="country__item-option">
                                <img
                                    v-if="selectedItemOption.illustration_src"
                                    :src="selectedItemOption.illustration_src"
                                    :alt="selectedItemOption.illustration_alt || selectedItemOption.name"
                                    class="country__item-icon"
                                >
                                {{ selectedItemOption.name }}
                            </span>
                        </template>
                        <template #option="{ option }">
                            <span class="country__item-option">
                                <img
                                    v-if="option.illustration_src"
                                    :src="option.illustration_src"
                                    :alt="option.illustration_alt || option.name"
                                    class="country__item-icon"
                                >
                                {{ option.name }}
                            </span>
                        </template>
                    </Select>
                </div>
                <div class="country__select-wrapper">
                    <Select
                        ref="municipalitySelect"
                        :modelValue="selectedMunicipality || null"
                        :options="groupedMunicipalities"
                        optionLabel="name"
                        optionValue="id"
                        optionGroupLabel="name"
                        optionGroupChildren="items"
                        inputId="selectedMunicipality"
                        placeholder="Hele Norge"
                        showClear
                        filter
                        filterPlaceholder="Søk etter kommune"
                        overlayClass="country__kommune-panel"
                        style="width: 100%"
                        @update:modelValue="municipalityId => $emit('update-selected-municipality', municipalityId ?? 0)"
                    >
                        <template #value="{ value, placeholder }">
                            <span v-if="value">{{ getMunicipality(value) }}</span>
                            <span v-else>{{ placeholder }}</span>
                        </template>
                        <template #optiongroup="{ option }">
                            <span
                                class="country__kommune-group-label"
                                @click="selectCounty(option)"
                            >{{ option.name }}</span>
                        </template>
                    </Select>
                </div>
                <div class="unit-switch unit-switch--on-dark">
                    <span class="unit-switch__label" :class="{ 'unit-switch__label--active': selectedUnit === 'kg' }">Kg</span>
                    <button
                        type="button"
                        class="unit-switch__track"
                        :class="{ 'unit-switch__track--liters': selectedUnit === 'liters' }"
                        role="switch"
                        :aria-checked="selectedUnit === 'liters'"
                        aria-label="Bytt mellom kg og liter"
                        @click="selectedUnit = selectedUnit === 'kg' ? 'liters' : 'kg'"
                    >
                        <span class="unit-switch__thumb"></span>
                    </button>
                    <span class="unit-switch__label" :class="{ 'unit-switch__label--active': selectedUnit === 'liters' }">Liter</span>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
    import Select from 'primevue/select';
    import Paginator from 'primevue/paginator';
    import { counties } from './counties';

    export default {
        components: {
            Select,
            Paginator,
        },
        emits: ['update-selected-municipality', 'update-selected-item', 'update-toplist-page'],
        props: [
            'selectedMunicipality',
            'selectedItem',
            'filteredData',
            'topList',
            'topListTotal',
            'topListPage',
            'topListPerPage',
            'loadingToplist',
            'data',
            'items',
            'activeGroup',
            'groupName',
            'season',
            'selectedYear'
        ],
        data() {
            return {
                showTooltip: false,
                tooltipX: 0,
                tooltipY: 0,
                userDetails: [],
                loadingUserDetails: false,
                currentUserId: null,
                tooltipTimeout: null,
                tooltipArrowPosition: 'left',
                arrowOffset: 0,
                isHoveringTooltip: false,
                isHoveringName: false,
                selectedUnit: 'kg',
                // Remembers every municipality name we've ever seen, so the
                // select/subtitle can still show a name for the currently
                // selected municipality even if a filter change (year, group,
                // item) makes it drop out of the current `data` response.
                municipalityNameCache: {},
                listHeightTimeout: null,
            }
        },
        watch: {
            data: {
                immediate: true,
                handler(locations) {
                    locations.forEach(location => {
                        this.municipalityNameCache[location.id] = location.name;
                    });
                },
            },
            selectedItem(newItemId) {
                if (Number(newItemId) === 0) {
                    return;
                }

                const item = this.items.find(i => Number(i.id) === Number(newItemId));

                if (item && item.uom) {
                    this.selectedUnit = item.uom.id === 1 ? 'kg' : 'liters';
                }
            },
            loadingToplist(isLoading) {
                const box = this.$refs.listBox;
                if (!box) {
                    return;
                }

                if (isLoading) {
                    // Freeze the box at its current height before the list is
                    // replaced, so swapping to a new page never flashes an
                    // empty/loading state at a different height.
                    box.style.minHeight = box.offsetHeight + 'px';
                    return;
                }

                this.$nextTick(() => {
                    if (!this.$refs.listBox) {
                        return;
                    }

                    this.settleListHeight();
                });
            },
        },
        mounted() {
            // Add click-away listener for mobile
            document.addEventListener('click', this.handleClickAway);
        },
        beforeDestroy() {
            // Clean up click-away listener
            document.removeEventListener('click', this.handleClickAway);
            if (this.tooltipTimeout) {
                clearTimeout(this.tooltipTimeout);
            }
            if (this.listHeightTimeout) {
                clearTimeout(this.listHeightTimeout);
            }
        },
        methods: {
            // The box is frozen at its pre-fetch height via the loadingToplist
            // watcher. Once the new list has rendered, transition it down/up
            // to fit the new content instead of snapping to it.
            settleListHeight() {
                const box = this.$refs.listBox;
                if (!box) {
                    return;
                }

                const frozenHeight = box.style.minHeight;
                box.style.minHeight = 'auto';
                const naturalHeight = box.offsetHeight;
                box.style.minHeight = frozenHeight;
                // Force a reflow so the frozen height is committed before we
                // transition to the natural height on the next frame.
                void box.offsetHeight;

                requestAnimationFrame(() => {
                    box.style.minHeight = naturalHeight + 'px';
                });

                if (this.listHeightTimeout) {
                    clearTimeout(this.listHeightTimeout);
                }
                this.listHeightTimeout = setTimeout(() => {
                    if (this.$refs.listBox) {
                        this.$refs.listBox.style.minHeight = '';
                    }
                }, 350);
            },
            getItems(municipality) {
                const items = municipality.itemGroups.filter(i => Number(i.id) === Number(this.activeGroup));

                return items.length > 0 ? parseInt(items[0].totals) : 0;
            },
            getMunicipality(id) {
                const county = counties.find(c => c.id === Number(id));
                if (county) {
                    return county.name;
                }

                const municipality = this.data.find(i => Number(i.id) === Number(id));

                return municipality?.name ?? this.municipalityNameCache[id] ?? '';
            },
            getItem(id) {
                return this.items.filter(b => Number(b.id) === Number(id))[0].name;
            },
            selectCounty(county) {
                this.$emit('update-selected-municipality', county.id);
                this.$refs.municipalitySelect?.hide();
            },
            formatName(name) {
                return name.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.substring(1)).join(' ');
            },
            formatDate(date) {
                const options = {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                };
                return new Date(date).toLocaleDateString('no-NO', options);
            },
            async showUserDetails(userId, event) {
                this.isHoveringName = true;

                // Clear any existing timeout
                if (this.tooltipTimeout) {
                    clearTimeout(this.tooltipTimeout);
                }

                // Add a small delay before showing tooltip
                this.tooltipTimeout = setTimeout(async () => {
                    console.log('Showing tooltip after delay');
                    this.showTooltip = true;
                    this.currentUserId = userId;
                    this.loadingUserDetails = true;

                    // Calculate tooltip position
                    this.calculateTooltipPosition(event);

                    try {
                        // Fetch user details
                        const itemParam = this.selectedItem != 0 ? `&item=${this.selectedItem}` : '';
                        const response = await fetch(`/fetch/user/${userId}/registrations?group=${this.activeGroup}&year=${this.selectedYear}${itemParam}`);
                        const data = await response.json();

                        // Extract registrations from the response
                        let allRegistrations = [];
                        if (data && data.length > 0) {
                            data.forEach(group => {
                                if (group.registrations) {
                                    allRegistrations = allRegistrations.concat(group.registrations);
                                }
                            });
                        }

                        // Sort by date (newest first)
                        allRegistrations.sort((a, b) => new Date(b.date) - new Date(a.date));

                        this.userDetails = allRegistrations;
                    } catch (error) {
                        console.error('Error fetching user details:', error);
                        this.userDetails = [];
                    } finally {
                        this.loadingUserDetails = false;
                    }
                }, 200); // 200ms delay
            },
            onNameMouseLeave() {
                this.isHoveringName = false;
                this.hideUserDetails();
            },
            hideUserDetails() {
                // Clear timeout if user moves away before tooltip shows
                if (this.tooltipTimeout) {
                    clearTimeout(this.tooltipTimeout);
                    this.tooltipTimeout = null;
                }

                if (this.isMobileDevice()) {
                    // On mobile, hide immediately when name is left
                    this.showTooltip = false;
                    this.userDetails = [];
                    this.currentUserId = null;
                    return;
                    
                } 

                setTimeout(() => {
                    if (!this.isHoveringTooltip && !this.isHoveringName) {
                        this.showTooltip = false;
                        this.userDetails = [];
                        this.currentUserId = null;
                    }
                }, 500);
            },
            keepTooltipVisible() {
                this.isHoveringTooltip = true;
                // Clear any hide timeout when hovering tooltip
                if (this.tooltipTimeout) {
                    clearTimeout(this.tooltipTimeout);
                    this.tooltipTimeout = null;
                }
            },
            onTooltipMouseLeave() {
                this.isHoveringTooltip = false;
                this.hideUserDetails();
            },
            toggleUserDetailsOnMobile(userId, event) {
                // Only handle click events on mobile devices
                if (window.innerWidth < 640) {
                    event.preventDefault();
                    if (this.showTooltip && this.currentUserId === userId) {
                        this.hideUserDetails();
                    } else {
                        this.showUserDetails(userId, event);
                    }
                }
            },
            isMobileDevice() {
                return window.innerWidth < 1024;
            },
            calculateTooltipPosition(event) {
                const targetElement = event.target;
                const listElement = targetElement.closest('.country__list');
                const columnElement = targetElement.closest('.country__overview-column');
                
                if (!listElement || !columnElement) {
                    return;
                }
                
                const columnRect = columnElement.getBoundingClientRect();
                const targetRect = targetElement.getBoundingClientRect();
                const listRect = listElement.getBoundingClientRect();
                

                const isMobile = this.isMobileDevice();
                const tooltipWidth = 320;
                const tooltipHeight = isMobile ? 300 : 400; // Now isMobile is defined
                
                let newX, newY;
                let arrowPosition = 'left';

                if (isMobile) {
                    // Mobile: position below/above the specific name that was clicked
                    const tooltipMaxWidth = Math.min(tooltipWidth, columnRect.width - 20);
                    newX = Math.max(10, Math.min(
                        columnRect.width - tooltipMaxWidth - 10,
                        targetRect.left - columnRect.left - (tooltipMaxWidth / 2) + (targetRect.width / 2)
                    ));
                    newY = targetRect.bottom - columnRect.top + 10; // Position below the specific name
                    arrowPosition = 'top';
                    
                    // Calculate arrow offset to point to the name center
                    const nameCenterX = targetRect.left - columnRect.left + (targetRect.width / 2);
                    const tooltipCenterX = newX + (tooltipMaxWidth / 2);
                    this.arrowOffset = nameCenterX - tooltipCenterX;
                    
                    // If would go off screen bottom, position above the name
                    if (columnRect.top + newY + tooltipHeight > window.innerHeight - 20) {
                        newY = targetRect.top - columnRect.top - tooltipHeight - 10; // Position above the specific name
                        arrowPosition = 'bottom';
                    }
                } else {
                    // Desktop: position to the right of the list
                    newX = listRect.right - columnRect.left + 20;
                    newY = targetRect.top - columnRect.top - 30;
                    arrowPosition = 'left';
                    this.arrowOffset = 0; // Reset arrow offset for desktop
                    
                    // If would go off right edge, position to the left
                    if (newX + tooltipWidth > window.innerWidth - columnRect.left - 20) {
                        newX = listRect.left - columnRect.left - tooltipWidth - 20;
                        arrowPosition = 'right';
                        
                        // If still off screen, position below
                        if (newX < 10) {
                            newX = Math.max(10, (listRect.left + listRect.right) / 2 - columnRect.left - tooltipWidth / 2);
                            newY = listRect.bottom - columnRect.top + 10;
                            arrowPosition = 'top';
                        }
                    }
                    
                    // Adjust vertical position to stay in viewport
                    const maxY = window.innerHeight - columnRect.top - tooltipHeight - 20;
                    const minY = -columnRect.top + 20;
                    
                    newY = Math.max(minY, Math.min(maxY, newY));
                }

                this.tooltipX = newX;
                this.tooltipY = newY;
                this.tooltipArrowPosition = arrowPosition;

            },
            handleClickAway(event) {
                // Only handle on mobile and if tooltip is visible
                if (window.innerWidth < 640 && this.showTooltip) {
                    const tooltip = document.querySelector('.country__tooltip');
                    const clickedElement = event.target;
                    
                    // Check if click is outside tooltip and not on a list name
                    if (tooltip && 
                        !tooltip.contains(clickedElement) && 
                        !clickedElement.closest('.country__list-name')) {
                        this.hideUserDetails();
                    }
                }
            }
        },
        computed: {
            filteredItems: function() {
                return this.items
                    .filter(i => Number(i.group_id) === Number(this.activeGroup))
                    .sort((a, b) => a.name.localeCompare(b.name, 'no'));
            },
            filterableMunicipalities: function() {
                return this.data.filter(municipality => {
                    const activeGroupData = municipality.itemGroups.find(g => Number(g.id) === Number(this.activeGroup));

                    return activeGroupData ? activeGroupData.hasPublicUsers : false;
                });
            },
            itemSelectOptions: function() {
                return this.filteredItems.length > 1
                    ? [{ id: 0, name: 'Alle typer' }, ...this.filteredItems]
                    : this.filteredItems;
            },
            selectedItemOption: function() {
                return this.itemSelectOptions.find(item => Number(item.id) === Number(this.selectedItem)) ?? null;
            },
            groupedMunicipalities: function() {
                return counties
                    .map(county => {
                        const municipalities = this.filterableMunicipalities.filter(municipality => Number(municipality.countyId) === county.id);

                        if (municipalities.length === 0) {
                            return null;
                        }

                        return {
                            id: county.id,
                            name: county.name,
                            items: municipalities,
                        };
                    })
                    .filter(Boolean);
            },
            rankedTopList: function() {
                return [...this.topList].sort((a, b) => b[this.selectedUnit] - a[this.selectedUnit]);
            },
tooltipStyles() {
                return {
                    position: 'absolute',
                    left: this.tooltipX + 'px',
                    top: this.tooltipY + 'px',
                    zIndex: 9999,
                    '--arrow-offset': this.arrowOffset + 'px'
                };
            },
            tooltipArrowClass() {
                return {
                    'country__tooltip-arrow--left': this.tooltipArrowPosition === 'left',
                    'country__tooltip-arrow--right': this.tooltipArrowPosition === 'right',
                    'country__tooltip-arrow--top': this.tooltipArrowPosition === 'top',
                    'country__tooltip-arrow--bottom': this.tooltipArrowPosition === 'bottom'
                };
            }
        }
    }
</script>